import npaw from "npawlib";
import { liveStatus } from "@/lib/metaUtil";
import debugLog from "@/lib/debugLog";
import config from "@/lib/config";
import { PLAYER_ERROR_CODES } from "@/lib/errors";

export default function attachNPAWPlugin(player, user, watch, debug) {
  if (typeof window !== "undefined" && config.npaw.accountCode) {
    const { episode, howtoPlay } = watch;
    const source = player.currentSource();

    const genre = episode?.genres?.[0] ?? "";

    const price = (() => {
      if (episode.rights_control_type === "entitled_only") {
        if (howtoPlay.courses.length > 0) {
          return howtoPlay.courses[0].defaultPriceWithoutTax;
        } else if (howtoPlay.products.length > 0) {
          return howtoPlay.products[0].priceWithoutTax;
        }
      } else {
        return 0;
      }
    })();

    const drm = (() => {
      if (source.keySystems?.["com.widevine.alpha"]) {
        return "widevine";
      }
      if (source.keySystems?.["com.apple.fps.1_0"]) {
        return "fairplay";
      }
      if (source.keySystems?.["com.microsoft.playready"]) {
        return "playready";
      }
    })();

    const errorCodes = PLAYER_ERROR_CODES.map((code) => String(code));

    npaw.Log.logLevel = debug ? npaw.Log.Level.VERBOSE : npaw.Log.Level.ERROR;

    const options = {
      accountCode: config.npaw.accountCode,
      "user.name": user?.hashedUserId || "Unregistered",
      "content.title": episode?.name?.substring(0, 100),
      "content.isLive": !!liveStatus(episode),
      "content.resource": source.src,
      "content.saga": episode?.tvShow?.name,
      "content.channel": episode?.channel?.name,
      "content.id": episode?.id,
      "content.genre": genre?.name,
      "content.price": price,
      "content.drm": drm,
      "errors.fatal": errorCodes,
      "app.name": "spooxweb",
      "app.releaseVersion": process.env.BUILDID,
    };
    debugLog("Npaw plugin set options", options);

    window.plugin = new npaw.Plugin(options);
    window.plugin.registerAdapter(
      player,
      JSON.stringify(StreaksPlayerAdapterJSON)
    );
  }
}

const StreaksPlayerAdapterJSON = {
  "adapter,getVersion": "return'7.0.1-streaks-js'",
  "adapter,checkExistsPlayer":
    "try{return this.checkExistsObjectOnPage(this.player.el())}catch(err){return true}",
  "adapter,getPlayhead": "return this.player.currentTime()",
  "adapter,getPlayrate": "return this.player.playbackRate()",
  "adapter,getFramesPerSecond": "return this.player.rendition().frameRate",
  "adapter,getDroppedFrames":
    "return this.player.getVideoPlaybackQuality().droppedVideoFrames",
  "adapter,getDuration": "return this.player.duration()",
  "adapter,getBitrate":
    "if(this.player.rendition()){return this.player.rendition().bitrate};return null",
  "adapter,getRendition":
    "return this.getNpawUtils().buildRenditionString(this.player.videoWidth(),this.player.videoHeight(),this.player.rendition().bitrate)",
  "adapter,getIsLive": "return this.player.isLive()",
  "adapter,getResource": "return this.player.src()",
  "adapter,getPlayerVersion":
    "return typeof streaksplayer!=='undefined'?streaksplayer.version:null",
  "adapter,getPlayerName": "return'Streaks player'",
  "adapter,getAudioCodec": "return this.player.rendition().audioCodec",
  "adapter,getVideoCodec": "return this.player.rendition().videoCodec",
  "adapter,registerListeners":
    "this.isPlaying=false;this.monitorPlayhead(false,false);this.references={'ready':this.readyListener.bind(this),'play':this.playListener.bind(this),'pause':this.pauseListener.bind(this),'playing':this.playingListener.bind(this),'join':this.joinListener.bind(this),'error':this.errorListener.bind(this),'seeking':this.seekingListener.bind(this),'seeked':this.seekedListener.bind(this),'waiting':this.waitingListener.bind(this),'ended':this.endedListener.bind(this),'timeupdate':this.timeupdateListener.bind(this),'stalled':this.stalledListener.bind(this),'canplay':this.canPlayListener.bind(this),'canplaythrough':this.canPlayThroughListener.bind(this),'loadedmediadata':this.loadedMediaDataListener.bind(this),'loadeddata':this.loadedDataListener.bind(this)};if(this.player&&this.references){for(var key in this.references){this.player.on(key,this.references[key])}}",
  "adapter,unregisterListeners":
    "this.isPlaying=false;if(this.monitor)this.monitor.stop();if(this.player&&this.references){for(var key in this.references){this.player.off(key,this.references[key]);delete this.references[key]}}",
  "adapter,readyListener,e": "",
  "adapter,playListener,e":
    "this.firePlayerLog('playListener',{});if(this.player){this.checkReadyState(this.player.readyState,'playListener')};this.fireStart({},'playListener');this.isPlaying=true",
  "adapter,timeupdateListener,e":
    "if((this.getPlayhead()>0.1||(this.getVideo()&&this.getVideo().getIsLive()))&&!this.player.error){this.fireStart({},'timeupdateListener');this.fireJoin({},'timeupdateListener');this.isPlaying=true}",
  "adapter,pauseListener,e":
    "this.firePlayerLog('pauseListener',{});this.firePause({},'pauseListener');this.isPlaying=false",
  "adapter,playingListener,e":
    "this.firePlayerLog('playingListener',{});this.fireResume({},'playingListener');this.fireSeekEnd({},'playingListener');if(this.flags.isBuffering){this.fireBufferEnd({},'playingListener')};this.fireJoin({},'playingListener');this.isPlaying=true",
  "adapter,joinListener,e":
    "this.firePlayerLog('joinListener',{});this.fireJoin({},'joinListener');this.isPlaying=true",
  "adapter,errorListener,e":
    "this.firePlayerLog('errorListener',{});var error=this.player.error()||{};var code=error.code;var msg=error.message;this.fireError(code,msg,undefined,undefined,'errorListener')",
  "adapter,seekingListener,e":
    "this.firePlayerLog('seekingListener',{});this.fireSeekBegin({},false,'seekingListener')",
  "adapter,seekedListener,e":
    "this.firePlayerLog('seekedListener',{});this.fireSeekEnd({},'seekedListener')",
  "adapter,waitingListener,e":
    "this.firePlayerLog('waitingListener',{});this.fireBufferBegin({},false,'waitingListener')",
  "adapter,stalledListener,e":
    "this.firePlayerLog('stalledListener',{});if(!this.isPlaying&&!this.isBuffering){this.fireBufferBegin({},false,'stalledListener')}",
  "adapter,endedListener,e":
    "this.firePlayerLog('endedListener',{});this.fireStop({},'endedListener');this.isPlaying=false",
  "adapter,loadedMediaDataListener,e":
    "if(this.player){this.checkReadyState(this.player.readyState,'loadedMediaDataListener')}",
  "adapter,canPlayListener,e":
    "if(this.player){this.checkReadyState(this.player.readyState,'canPlayListener')}",
  "adapter,canPlayThroughListener,e":
    "if(this.player){this.checkReadyState(this.player.readyState,'canPlayThroughListener')}",
  "adapter,loadedDataListener,e":
    "if(this.player){this.checkReadyState(this.player.readyState,'loadedDataListener')}",
  "adapter,checkReadyState,readyState,triggeredEvent":
    "try{if(this.getPlugin()&&this.getPlugin().getReadyStateMonitorEnabled()){if(readyState){if(readyState>3&&!this.flags.isSeeking){if(this.flags.isBuffering){this.fireBufferEnd({},triggeredEvent+'-readyState')}else if(!this.flags.isJoined){this.fireJoin({},triggeredEvent+'-readyState')}}else if(readyState<4&&!this.flags.isBuffering){this.fireBufferBegin({},false,triggeredEvent+'-readyState',true)}}}}catch(e){this.log.error('Can\\'t check readyState property correctly')}",
};
